import { inject, Injectable } from '@angular/core';
import { DoodProductModel, IDeclinableCategory } from '@core/models/product.model';
import { DeclinableProductCart } from '@shared/local-store/cart/cart-draft-declinable-product.store';
import { ProductsService } from '@core/services/products/products.service';
import { combineLatest, map, Observable, take } from 'rxjs';
import { StepInfo } from '@core/models/stepper.model';
import { SimpleProductCart } from '../cart/cart-draft-simple-product.store';
import { IAdditionGroup } from '@core/models/shop.model';
import { ProductStoreRefiner } from '@common/refiners/product.refiner';
import { getAdditionGroupPosition } from './util/stepper-util';
import { ICartDeclinableAdditionsItem } from '@core/models/cart.model';

type StepPosition =
  | {
      categoryPosition: number;
      additionGroupPosition: number | undefined;
    }
  | {
      additionGroupPosition: number;
    };

type StepInfoWithIndex = StepInfo & StepPosition;

type ProductStepInfo = StepInfoWithIndex & {
  id: string;
  quantity: number;
};

export type ProductStepDetails = ProductStepInfo & {
  product: DoodProductModel | undefined;
};

@Injectable({
  providedIn: 'root',
})
export class CompositionStepperUtil {
  private readonly productsService = inject(ProductsService);
  private readonly productStoreRefiner = inject(ProductStoreRefiner);

  public declinableProductStepperComposition$({
    categories,
    cart,
  }: {
    categories: IDeclinableCategory[];
    cart: DeclinableProductCart;
  }): Observable<ProductStepDetails[] | undefined> {
    const cartWithSimpleProductGroupedQuantity = {
      ...cart,
      products: cart.products?.reduce((acc, item) => {
        const existingItem = acc.find(i => i.id === item.id);
        if (existingItem) {
          existingItem.quantity += item.quantity;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, [] as ICartDeclinableAdditionsItem[]),
    };
    return combineLatest({
      allProducts: this.productsService.selectAll$(),
      additionsGroups: this.productStoreRefiner.selectAdditionsGroups,
    }).pipe(
      take(1),
      map(({ allProducts, additionsGroups }) => {
        return (
          this.declinableSelectableProductWithQuantityIds({
            cart: cartWithSimpleProductGroupedQuantity,
            categories,
            allProducts,
            additionsGroups,
          })
            ?.map(
              selectedProductData =>
                ({
                  product: allProducts.find(allProduct => allProduct.id === selectedProductData.id),
                  ...selectedProductData,
                }) satisfies ProductStepDetails,
            )
            .map(data => {
              const result = {
                data,
                categoryPosition: categories.findIndex(category => {
                  return category.store_id === data.categoryId;
                }),
                simpleProductId: data.simpleProductId,
                additionGroupPosition: allProducts
                  .find(product => product.id === data.simpleProductId)
                  ?.additions?.products.findIndex(id => id === data.id),
              };

              return result;
            })
            .sort((a, b) => {
              if (a.categoryPosition === b.categoryPosition) {
                return (a.additionGroupPosition ?? 0) - (b.additionGroupPosition ?? 0);
              }

              return a.categoryPosition - b.categoryPosition;
            })
            .map(({ data }) => data) ?? []
        );
      }),
    );
  }

  private declinableSelectableProductWithQuantityIds({
    cart,
    categories,
    allProducts,
    additionsGroups,
  }: {
    cart: DeclinableProductCart;
    categories: IDeclinableCategory[];
    allProducts: DoodProductModel[];
    additionsGroups: IAdditionGroup[];
  }): ProductStepInfo[] | undefined {
    const productsIdToDisplay = cart.products
      ?.map(item => {
        const isExclusiveCategoryProduct =
          categories.find(category => {
            return category.products.find(categoryProduct => categoryProduct === item.id);
          })?.products.length === 1;
        const additionIdWithQuantity =
          item.separate_additions_by_group
            ?.map(group => {
              const countByAdditionId =
                group.selected.reduce<Record<string, number>>((acc, id) => {
                  acc[id] = (acc[id] || 0) + 1;
                  return acc;
                }, {}) ?? {};
              const product = allProducts.find(product => product.id === item.id);

              const additionGroupPosition = getAdditionGroupPosition({
                product,
                allProducts,
                groupId: group.id,
                additionsGroups,
              });
              return Object.entries(countByAdditionId).map(([id, quantity]) => {
                // ! additionGroupPosition can be 0, so in this case check the order of the additionProductId from the simpleproduct additions
                const stepInfo: StepInfoWithIndex = {
                  categoryPosition:
                    categories.findIndex(category => category.store_id === item.store_id) ?? -1,
                  additionGroupPosition,
                  categoryId: item.store_id,
                  simpleProductId: item.id,
                  additionsGroupId: group.id,
                };
                return {
                  id,
                  quantity,
                  ...stepInfo,
                };
              });
            })
            .flat() ?? [];
        const stepInfo: StepInfoWithIndex = {
          categoryId: item.store_id,
          categoryPosition:
            categories.findIndex(category => category.store_id === item.store_id) ?? -1,
          additionGroupPosition: undefined, // todoR  check if we need to set, on black burger menu
          // simpleProductId: item.id,
        };
        return [
          !isExclusiveCategoryProduct
            ? [
                {
                  id: item.id,
                  quantity: item.quantity,
                  ...stepInfo,
                },
              ]
            : [],
          ...additionIdWithQuantity,
        ];
      })
      .flat();
    const result = productsIdToDisplay?.flat();
    return result;
  }

  public simpleProductStepperComposition$({
    cart,
    productAdditionIds,
  }: {
    cart: SimpleProductCart;
    productAdditionIds: string[];
  }): Observable<ProductStepDetails[] | undefined> {
    return this.productsService.selectAll$().pipe(
      take(1),
      map(allProducts => {
        const result =
          this.simpleProductSelectableStepsWithProductWithQuantity({ cart, productAdditionIds })
            ?.map(selectedProductData => ({
              product: allProducts.find(allProduct => allProduct.id === selectedProductData.id),
              ...selectedProductData,
            }))
            .map(data => ({
              data,
              additionGroupPosition: productAdditionIds.findIndex(id => id === data.id),
            }))
            .sort((a, b) => (a.additionGroupPosition ?? 0) - (b.additionGroupPosition ?? 0))
            .map(({ data }) => data) ?? [];
        return result;
      }),
    );
  }

  private simpleProductSelectableStepsWithProductWithQuantity({
    cart,
    productAdditionIds,
  }: {
    cart: SimpleProductCart;
    productAdditionIds: string[];
  }): ProductStepInfo[] | undefined {
    const additionIdWithQuantity =
      cart.separate_additions_by_group
        ?.map(group => {
          const countByAdditionId =
            group.selected.reduce<Record<string, number>>((acc, id) => {
              acc[id] = (acc[id] || 0) + 1;
              return acc;
            }, {}) ?? {};
          return Object.entries(countByAdditionId).map(([id, quantity]) => {
            const stepInfo: StepInfoWithIndex = {
              simpleProductId: cart.item_id,
              additionsGroupId: group.id,
              additionGroupPosition: productAdditionIds.findIndex(id => id === group.id),
            };
            return {
              id,
              quantity,
              ...stepInfo,
            };
          });
        })
        .flat() ?? [];
    return additionIdWithQuantity;
  }
}
